import React from "react";
import { Link } from "react-router-dom";
import { HeartHandshake, ArrowRight, ShieldCheck } from "lucide-react";
import { PageHeader } from "../components/PageHeader";
import { ServiceCard } from "../components/ServiceCard";
import { PSYCHOLOGY_SERVICES, CLINIC } from "../lib/clinic";

const PsychologyServices = () => {
  return (
    <div data-testid="psychology-page">
      <PageHeader
        eyebrow="Psychology"
        title="A calm, judgment-free space to heal."
        subtitle="Structured therapy and counseling for anxiety, depression, panic, stress, sleep and relationship concerns — strictly confidential."
        breadcrumbs={["Services", "Psychology"]}
      />

      <section className="section-y bg-paper">
        <div className="container-x">
          <div className="flex items-center gap-5 mb-16">
            <HeartHandshake className="w-7 h-7 text-brand-goldDark" strokeWidth={1.3} />
            <div>
              <div className="heading-serif text-2xl md:text-3xl font-medium text-brand-navyDark">
                What we help with
              </div>
              <p className="text-[13px] text-brand-muted tracking-wide">Evidence-based therapy, completely confidential.</p>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-stone-200">
            {PSYCHOLOGY_SERVICES.map((s, i) => (
              <ServiceCard key={s.title} {...s} index={i} accent="gold" />
            ))}
          </div>
        </div>
      </section>

      <section className="section-y bg-paper-soft">
        <div className="container-x">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <div className="eyebrow mb-5">Therapy Approach</div>
              <h2 className="heading-serif text-4xl md:text-5xl font-medium text-brand-navyDark leading-[1.08] mb-6">
                Structured, gentle and effective.
              </h2>
              <p className="text-brand-body leading-[1.85] mb-7 text-[15px]">
                Each session blends careful listening with practical tools — cognitive
                techniques, breathing & relaxation work, behavioural strategies and where
                needed, medication input from a neurologist's lens.
              </p>
              <div className="flex items-center gap-4 border-l-2 border-brand-goldDark pl-5 py-2">
                <ShieldCheck className="w-5 h-5 text-brand-goldDark shrink-0" strokeWidth={1.5} />
                <div>
                  <div className="font-medium text-brand-navyDark text-[14px]">100% Confidential</div>
                  <div className="text-brand-muted text-[12px]">Your privacy is sacred. Always.</div>
                </div>
              </div>
            </div>
            <ol className="lg:col-span-6 lg:col-start-7 space-y-1">
              {[
                "Understanding your concern & goals",
                "Building a therapy roadmap together",
                "Weekly / bi-weekly structured sessions",
                "Practical tools & homework between sessions",
                "Progress review & long-term wellness",
              ].map((step, i) => (
                <li
                  key={i}
                  className="flex items-start gap-6 py-5 border-b border-stone-200 last:border-0"
                >
                  <span className="heading-serif text-2xl text-brand-goldDark font-medium leading-none w-8 shrink-0">
                    0{i + 1}
                  </span>
                  <span className="text-brand-navyDark text-[16px] leading-relaxed">{step}</span>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <section className="section-y bg-paper">
        <div className="container-x max-w-2xl">
          <h2 className="heading-serif text-4xl md:text-5xl font-medium text-brand-navyDark mb-6 leading-[1.08]">
            Take the first step — <span className="italic text-brand-goldDark">gently</span>.
          </h2>
          <p className="text-brand-body mb-10 text-[16px] leading-[1.8]">
            Asking for help is a sign of strength. Let's talk.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/contact#book" className="btn-primary">
              Book Psychology Session <ArrowRight className="w-3.5 h-3.5" />
            </Link>
            <a href={CLINIC.whatsapp} target="_blank" rel="noreferrer" className="btn-secondary">
              WhatsApp Quietly
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PsychologyServices;
